import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
} from '@angular/common/http';
import { Injectable, Injector } from '@angular/core';
import { Router } from '@angular/router';
import { Observable, catchError, switchMap, throwError } from 'rxjs';

import { AuthService } from '../services/auth.service';
import { isTrustedApiRequest } from '../util/request-auth.util';
import {
  AUTH_SESSION_RETRY_ATTEMPTED,
  SKIP_AUTH_SESSION,
} from './auth-session-context';

/**
 * Recovers once from an expired access JWT. Refresh credentials are httpOnly
 * cookies, so this interceptor never reads or persists them in JavaScript.
 */
@Injectable()
export class AuthSessionInterceptor implements HttpInterceptor {
  constructor(private injector: Injector, private router: Router) {}

  intercept(
    request: HttpRequest<unknown>,
    next: HttpHandler
  ): Observable<HttpEvent<unknown>> {
    if (
      request.context.get(SKIP_AUTH_SESSION) ||
      request.context.get(AUTH_SESSION_RETRY_ATTEMPTED) ||
      !isTrustedApiRequest(request.url) ||
      request.url.includes('/rest/auth/')
    ) {
      return next.handle(request);
    }

    return next.handle(request).pipe(
      catchError((error: unknown) => {
        if (!(error instanceof HttpErrorResponse) || error.status !== 401) {
          return throwError(() => error);
        }

        const authService = this.injector.get(AuthService);
        return authService.refreshAccessToken().pipe(
          // A refresh-service outage should not disguise the request that
          // originally failed authentication. This intentionally runs before
          // switchMap so a failed retry keeps its own response error.
          catchError(() => throwError(() => error)),
          switchMap((refreshed) => {
            const accessToken = authService.getToken();
            if (!refreshed || !accessToken) {
              if (!this.router.url.startsWith('/login')) {
                this.router.navigate(['/login'], {
                  queryParams: { returnUrl: this.router.url },
                });
              }
              return throwError(() => error);
            }

            return next.handle(
              request.clone({
                setHeaders: { Authorization: `Bearer ${accessToken}` },
                context: request.context.set(
                  AUTH_SESSION_RETRY_ATTEMPTED,
                  true
                ),
              })
            );
          })
        );
      })
    );
  }
}
